import { useState, useEffect, useCallback, useRef } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { contentModuleAPI } from '../services/api';
import Icon from '../components/Icon';

const styles = {
  container: {
    width: '100%'
  },
  header: {
    marginBottom: '20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  title: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  subtitle: {
    fontSize: '13px',
    color: '#64748b',
    marginTop: '2px'
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    overflow: 'hidden'
  },
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    borderBottom: '1px solid #e2e8f0',
    gap: '12px'
  },
  searchInput: {
    flex: 1,
    maxWidth: '300px',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none'
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    whiteSpace: 'nowrap'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    backgroundColor: '#f8fafc',
    borderBottom: '1px solid #e2e8f0'
  },
  td: {
    padding: '16px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: '14px',
    color: '#1e293b'
  },
  moduleId: {
    fontFamily: 'Monaco, Consolas, monospace',
    fontSize: '13px',
    color: '#6366f1',
    backgroundColor: '#eef2ff',
    padding: '4px 8px',
    borderRadius: '4px'
  },
  statusBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  activeStatus: {
    backgroundColor: '#dcfce7',
    color: '#166534'
  },
  inactiveStatus: {
    backgroundColor: '#fee2e2',
    color: '#991b1b'
  },
  actionButtons: {
    display: 'flex',
    gap: '8px'
  },
  iconButton: {
    padding: '8px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: '#64748b',
    transition: 'all 0.15s ease'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#64748b'
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  emptyText: {
    fontSize: '16px',
    fontWeight: '500',
    marginBottom: '8px'
  },
  emptySubtext: {
    fontSize: '14px'
  },
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },
  modalHeader: {
    padding: '20px 24px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b'
  },
  modalBody: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1
  },
  modalFooter: {
    padding: '16px 24px',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px'
  },
  formGroup: {
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#475569',
    marginBottom: '6px'
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    minHeight: '80px',
    boxSizing: 'border-box'
  },
  button: {
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    border: 'none'
  },
  primaryButton: {
    backgroundColor: '#4f46e5',
    color: '#fff'
  },
  secondaryButton: {
    backgroundColor: '#f1f5f9',
    color: '#475569'
  },
  errorBox: {
    padding: '12px 16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    color: '#dc2626',
    marginBottom: '16px',
    fontSize: '14px'
  },
  uploadArea: {
    border: '2px dashed #cbd5e1',
    borderRadius: '8px',
    padding: '20px',
    textAlign: 'center',
    cursor: 'pointer',
    backgroundColor: '#f8fafc',
    transition: 'all 0.2s ease'
  },
  uploadAreaActive: {
    borderColor: '#4f46e5',
    backgroundColor: '#f5f3ff'
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    marginTop: '8px'
  },
  fileName: {
    flex: 1,
    fontSize: '14px',
    color: '#1e293b',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  removeFileBtn: {
    padding: '4px 8px',
    backgroundColor: '#fee2e2',
    color: '#dc2626',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer'
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    padding: '16px',
    borderTop: '1px solid #e2e8f0'
  },
  pageButton: {
    padding: '8px 12px',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontSize: '14px'
  },
  pageButtonActive: {
    backgroundColor: '#4f46e5',
    color: '#fff',
    borderColor: '#4f46e5'
  },
  fileUrl: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  linkButton: {
    padding: '4px 8px',
    backgroundColor: '#dbeafe',
    color: '#1d4ed8',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    textDecoration: 'none'
  }
};

function ContentModules() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useUrlState('page', 1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [showModal, setShowModal] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [formData, setFormData] = useState({
    moduleId: '',
    title: '',
    description: '',
    icon: '',
    sortOrder: 0,
    isActive: true
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const fetchModules = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (search) params.search = search;

      const response = await contentModuleAPI.getAll(params);
      setModules(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error('Error fetching modules:', err);
    } finally {
      setLoading(false);
    }
  }, [search, page]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const handleAdd = () => {
    setEditingModule(null);
    setFormData({
      moduleId: '',
      title: '',
      description: '',
      icon: '',
      sortOrder: 0,
      isActive: true
    });
    setSelectedFile(null);
    setError('');
    setShowModal(true);
  };

  const handleEdit = (module) => {
    setEditingModule(module);
    setFormData({
      moduleId: module.moduleId,
      title: module.title,
      description: module.description || '',
      icon: module.icon || '',
      sortOrder: module.sortOrder || 0,
      isActive: module.isActive
    });
    setSelectedFile(null);
    setError('');
    setShowModal(true);
  };

  const handleDelete = async (module) => {
    if (!window.confirm(`Are you sure you want to delete "${module.title}"?`)) return;

    try {
      await contentModuleAPI.delete(module.id);
      fetchModules();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete module');
    }
  };

  const handleToggleStatus = async (module) => {
    try {
      await contentModuleAPI.toggleStatus(module.id);
      fetchModules();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to toggle status');
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (file) => {
    const validExtensions = ['.html', '.htm', '.json'];
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validExtensions.includes(ext)) {
      setError('Only HTML and JSON files are allowed');
      return;
    }
    setSelectedFile(file);
    setError('');
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleSave = async () => {
    try {
      setError('');
      setSaving(true);

      if (!formData.moduleId || !formData.title) {
        setError('Module ID and title are required');
        setSaving(false);
        return;
      }

      if (!editingModule && !selectedFile) {
        setError('HTML file is required');
        setSaving(false);
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('moduleId', formData.moduleId);
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('icon', formData.icon);
      formDataToSend.append('sortOrder', formData.sortOrder);
      formDataToSend.append('isActive', formData.isActive);

      if (selectedFile) {
        formDataToSend.append('file', selectedFile);
      }

      if (editingModule) {
        await contentModuleAPI.update(editingModule.id, formDataToSend);
      } else {
        await contentModuleAPI.create(formDataToSend);
      }

      setShowModal(false);
      fetchModules();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save module');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFullUrl = (path) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5051';
    return `${baseUrl}${path}`;
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Content Modules</h1>
          <p style={styles.subtitle}>Manage HTML/JSON content pages for mobile app</p>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.toolbar}>
          <input
            type="text"
            placeholder="Search modules..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            style={styles.searchInput}
          />
          <button style={styles.addButton} onClick={handleAdd}>
            <span>+</span> Add Module
          </button>
        </div>

        {loading ? (
          <div style={styles.emptyState}>Loading...</div>
        ) : modules.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}><Icon name="file" size={40} /></div>
            <div style={styles.emptyText}>No content modules found</div>
            <div style={styles.emptySubtext}>Add your first content module (HTML or JSON)</div>
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Order</th>
                  <th style={styles.th}>Module ID</th>
                  <th style={styles.th}>Title</th>
                  <th style={styles.th}>Content File</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Created</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {modules.map((module) => (
                  <tr key={module.id}>
                    <td style={styles.td}>{module.sortOrder}</td>
                    <td style={styles.td}>
                      <span style={styles.moduleId}>{module.moduleId}</span>
                    </td>
                    <td style={styles.td}>
                      <div>{module.title}</div>
                      {module.description && (
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                          {module.description}
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.fileUrl}>
                        <span style={{
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: '600',
                          marginRight: '8px',
                          backgroundColor: module.contentType === 'json' ? '#fef3c7' : '#dbeafe',
                          color: module.contentType === 'json' ? '#92400e' : '#1d4ed8'
                        }}>
                          {module.contentType?.toUpperCase() || 'HTML'}
                        </span>
                        <a
                          href={getFullUrl(module.htmlFile || module.jsonFile)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={styles.linkButton}
                        >
                          View File
                        </a>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.statusBadge,
                        ...(module.isActive ? styles.activeStatus : styles.inactiveStatus)
                      }}>
                        {module.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ ...styles.td, color: '#64748b' }}>
                      {formatDate(module.createdAt)}
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actionButtons}>
                        <button
                          style={styles.iconButton}
                          onClick={() => handleToggleStatus(module)}
                          title={module.isActive ? 'Deactivate' : 'Activate'}
                        >
                          <Icon name="dot" size={15} style={{ color: module.isActive ? '#16a34a' : '#94a3b8' }} />
                        </button>
                        <button
                          style={styles.iconButton}
                          onClick={() => handleEdit(module)}
                          title="Edit"
                        >
                          <Icon name="edit" size={15} />
                        </button>
                        <button
                          style={styles.iconButton}
                          onClick={() => handleDelete(module)}
                          title="Delete"
                        >
                          <Icon name="trash" size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination.totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  style={styles.pageButton}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <span style={{ fontSize: '14px', color: '#64748b' }}>
                  Page {page} of {pagination.totalPages}
                </span>
                <button
                  style={styles.pageButton}
                  onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingModule ? 'Edit Content Module' : 'Add Content Module'}
              </h2>
              <button
                style={{ ...styles.iconButton, fontSize: '20px' }}
                onClick={() => setShowModal(false)}
              >
                <Icon name="close" size={16} />
              </button>
            </div>

            <div style={styles.modalBody}>
              {error && <div style={styles.errorBox}>{error}</div>}

              <div style={styles.formGroup}>
                <label style={styles.label}>Module ID *</label>
                <input
                  type="text"
                  value={formData.moduleId}
                  onChange={(e) => setFormData({ ...formData, moduleId: e.target.value })}
                  style={styles.input}
                  placeholder="e.g., about-us, terms, road-rules"
                  disabled={!!editingModule}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  style={styles.input}
                  placeholder="Display title for this module"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={styles.textarea}
                  placeholder="Brief description (optional)"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Icon (optional)</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  style={styles.input}
                  placeholder="Icon name or URL"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Sort Order</label>
                <input
                  type="number"
                  value={formData.sortOrder}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                  style={styles.input}
                  placeholder="0"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>
                  Content File (HTML or JSON) {!editingModule && '*'}
                  {editingModule && ' (leave empty to keep current)'}
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".html,.htm,.json"
                  onChange={handleFileInputChange}
                  style={{ display: 'none' }}
                />
                <div
                  style={{
                    ...styles.uploadArea,
                    ...(dragActive ? styles.uploadAreaActive : {})
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <div style={{ marginBottom: '8px', color: '#94a3b8', display: 'flex', justifyContent: 'center' }}><Icon name="folder" size={32} /></div>
                  <div style={{ fontSize: '14px', color: '#64748b' }}>
                    Drag and drop HTML or JSON file here or click to browse
                  </div>
                </div>
                {selectedFile && (
                  <div style={styles.fileInfo}>
                    <Icon name="file" size={18} style={{ color: '#64748b' }} />
                    <span style={styles.fileName}>{selectedFile.name}</span>
                    <button
                      style={styles.removeFileBtn}
                      onClick={() => setSelectedFile(null)}
                    >
                      Remove
                    </button>
                  </div>
                )}
                {editingModule && !selectedFile && (editingModule.htmlFile || editingModule.jsonFile) && (
                  <div style={{ ...styles.fileInfo, backgroundColor: '#f0fdf4' }}>
                    <Icon name="check" size={18} style={{ color: '#16a34a' }} />
                    <span style={styles.fileName}>
                      Current file: {(editingModule.htmlFile || editingModule.jsonFile).split('/').pop()}
                      <span style={{
                        marginLeft: '8px',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: '600',
                        backgroundColor: editingModule.contentType === 'json' ? '#fef3c7' : '#dbeafe',
                        color: editingModule.contentType === 'json' ? '#92400e' : '#1d4ed8'
                      }}>
                        {editingModule.contentType?.toUpperCase() || 'HTML'}
                      </span>
                    </span>
                  </div>
                )}
              </div>

              <div style={styles.formGroup}>
                <div style={styles.checkbox}>
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                  <label htmlFor="isActive" style={{ fontSize: '14px', color: '#475569' }}>
                    Active (visible in mobile app)
                  </label>
                </div>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : (editingModule ? 'Update' : 'Create')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ContentModules;
